import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FolderOpen, Folder, Tag, EnvelopeSimple, HardDrive, CodeSimple, GithubLogo } from '@phosphor-icons/react';
import { BaseEntityType, EntityTypeUtils } from '../types/entities';
import { Icon } from '../components/Icon';
import FileIcon from '../components/FileIcon';
import { iconService } from '../services/iconService';
import useEntityStore from '../stores/entityStore';
import { getSenderAvatarOptions } from '../apps/MailApp/utils/senderAvatar';
import { acquireFaviconSlot } from '../utils/faviconQueue';

/**
 * Small avatar component for Contact entities. Reuses the same avatar resolution
 * logic as SenderAvatar (stored avatarUrl → company favicon → Gravatar proxy → initials).
 */
const ContactEntityIcon: React.FC<{ entity: BaseEntityType; size: number }> = ({ entity, size }) => {
  const name = entity.name || '';
  const email = (entity.skeleton as any)?.email?.trim() || '';
  const options = getSenderAvatarOptions({ name, email });

  const [loadSrc, setLoadSrc] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const releaseRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const url = options.imageUrl;
    if (!url) {
      setLoadSrc(null);
      setImageFailed(false);
      return;
    }

    let cancelled = false;

    acquireFaviconSlot().then((release) => {
      if (cancelled) {
        release();
        return;
      }
      releaseRef.current = release;
      setLoadSrc(url);
    });

    return () => {
      cancelled = true;
      releaseRef.current?.();
      releaseRef.current = null;
      setLoadSrc(null);
      setImageFailed(false);
    };
  }, [options.imageUrl]);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    releaseRef.current?.();
    releaseRef.current = null;
    // Google favicon service returns a tiny default globe — treat as failed
    if (e.currentTarget.naturalWidth <= 16) setImageFailed(true);
  };

  const handleError = () => {
    releaseRef.current?.();
    releaseRef.current = null;
    setImageFailed(true);
  };

  const showImage = !!loadSrc && !imageFailed;

  const AVATAR_COLORS = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#06b6d4'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  const bg = showImage ? 'transparent' : AVATAR_COLORS[hash % AVATAR_COLORS.length];

  const initials = name
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      minWidth: size,
      borderRadius: '50%',
      background: bg,
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      {showImage ? (
        <img
          src={loadSrc}
          alt=""
          referrerPolicy="no-referrer"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onLoad={handleLoad}
          onError={handleError}
        />
      ) : (
        <span style={{
          color: '#fff',
          fontSize: Math.max(7, Math.round(size * 0.45)),
          fontWeight: 600,
          lineHeight: 1,
          userSelect: 'none',
        }}>
          {initials}
        </span>
      )}
    </span>
  );
};

interface UseEntityIconProps {
  expandedGroups?: Set<string>;
  size?: number;
}

/**
 * Custom hook that provides unified entity icon handling
 * 
 * ARCHITECTURE:
 * - Groups: Special case for folder open/closed state (handled here)
 * - Files: Delegates to FileIcon component (which routes media to iconService, code/docs to VSCode icons)
 * - Docs: Custom SVG icon
 * - Others: Uses Icon component with entity prop (which uses iconService)
 * 
 * ICON RESOLUTION PATH:
 * Component → useEntityIcon → FileIcon/Icon → iconService (for media files)
 * 
 * This ensures consistent icons across:
 * - EntitySidebar
 * - WindowEntityTabs
 * - ContextSelector
 * - All other components using this hook
 */
export const useEntityIcon = ({ expandedGroups = new Set(), size = 16 }: UseEntityIconProps = {}) => {
  const entities = useEntityStore(state => state.entities);
  
  /**
   * Helper to get Icon size string based on numeric size
   */
  const getIconSize = (): 'small' | 'medium' | 'large' | 'xlarge' => {
    if (size <= 22) return 'small';
    if (size <= 30) return 'medium';
    if (size <= 32) return 'large';
    return 'xlarge';
  };
  
  /**
   * Check if we need custom dimensions (size > standard xlarge 30px)
   */
  const needsCustomDimensions = size > 30;
  
  /**
   * Get icon for an entity (synchronous, optimized for rendering)
   */
  const getEntityIcon = useCallback((entity: BaseEntityType): React.ReactNode => {
    // Defensive: Handle entities without entityType
    if (!entity?.entityType) {
      return <Folder size={size} weight="fill" />;
    }
    
    // ✅ SPECIAL CASE: App entities - use direct icon from entity
    if (entity.entityType === 'App') {
      const appIcon = (entity as any).icon || (entity as any).skeleton?.icon;
      if (appIcon) {
        return <Icon icon={appIcon} size={getIconSize()} width={needsCustomDimensions ? size : undefined} height={needsCustomDimensions ? size : undefined} />;
      }
      // Fallback for apps without icons
      return <Icon icon={{ type: 'phosphor', phosphorIcon: 'AppWindow', phosphorWeight: 'duotone' }} size={getIconSize()} />;
    }
    
    // Special case: Synthetic "Chats" group gets a chat icon instead of folder
    if (entity._id === '__spacechat__' || 
        entity._id === '__chats_folder__' || 
        entity._id === '__drive_chats_folder__' ||
        entity._id.startsWith('__drive_chats_folder_')) {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'Chat',
            phosphorWeight: 'fill',
            metadata: {
              source: 'entity',
              entityType: 'Group',
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined}
        />
      );
    }
    
    // Special case: LocalMount with expansion state
    if (entity.entityType === 'LocalMount') {
      const isExpanded = expandedGroups.has(entity._id);
      return isExpanded
        ? <HardDrive size={size} weight="fill" />
        : <HardDrive size={size} weight="duotone" />;
    }

    // Special case: Codebase — GitHub uses GithubLogo, local uses CodeSimple
    if (entity.entityType === 'Codebase') {
      const source = (entity as any).skeleton?.source;
      return source === 'github'
        ? <GithubLogo size={size} weight="fill" />
        : <CodeSimple size={size} weight="duotone" />;
    }
    
    // Special case: Groups with expansion state (folder open/closed)
    if (entity.entityType === 'Group') {
      // Entity type folders show the icon of the type they contain
      const schemaIcon = (entity.skeleton as any)?.isEntityTypeFolder && (entity.skeleton as any)?.icon;
      if (schemaIcon) {
        return (
          <Icon
            icon={{ type: 'phosphor', phosphorIcon: schemaIcon, phosphorWeight: 'duotone',
              metadata: { source: 'entity', entityType: 'Group', isAppIcon: false } }}
            size={getIconSize()}
            width={needsCustomDimensions ? size : undefined}
            height={needsCustomDimensions ? size : undefined}
          />
        );
      }
      const isExpanded = expandedGroups.has(entity._id);
      return isExpanded 
        ? <FolderOpen size={size} weight="fill" /> 
        : <Folder size={size} weight="fill" />;
    }
    
    // Special case: DriveRoot entities get HardDrives icon
    if (entity.entityType === 'DriveRoot') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'HardDrives',
            phosphorWeight: 'duotone',
            metadata: {
              source: 'entity',
              entityType: 'DriveRoot',
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined}
        />
      );
    }

    // Special case: Space entities get Cards icon
    if (entity.entityType === 'Space') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'Cards',
            phosphorWeight: 'duotone',
            metadata: {
              source: 'entity',
              entityType: 'Space',
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // Special case: Project entities get dynamic progress circle with pie chart
    if (entity.entityType === 'Project') {
      // Find all tasks for this project
      // Note: Tasks have hidden:true but are still in entityStore.entities (filtered only at display level)
      const projectTasks = entities.filter((e: BaseEntityType) => 
        e.entityType === 'Task' && 
        (e.skeleton as any)?.projectId === entity._id
      );
      
      const completedCount = projectTasks.filter((t: BaseEntityType) => 
        (t.skeleton as any)?.completed === true
      ).length;
      const totalCount = projectTasks.length;
      
      // Calculate SVG properties scaled to icon size
      const radius = size === 16 ? 6 : (size / 16) * 6;
      const strokeWidth = size === 16 ? 1.5 : (size / 16) * 1.5;
      const center = size / 2;
      const pieRadius = size === 16 ? 5 : (size / 16) * 5;
      
      // Helper to create pie slice path
      const createPieSlice = (startAngle: number, endAngle: number): string => {
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;
        
        const x1 = center + pieRadius * Math.cos(startRad);
        const y1 = center + pieRadius * Math.sin(startRad);
        const x2 = center + pieRadius * Math.cos(endRad);
        const y2 = center + pieRadius * Math.sin(endRad);
        
        const largeArc = endAngle - startAngle > 180 ? 1 : 0;
        
        return `M ${center} ${center} L ${x1} ${y1} A ${pieRadius} ${pieRadius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      };
      
      const isComplete = totalCount > 0 && completedCount === totalCount;
      
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#3b82f6"
            strokeWidth={strokeWidth}
          />
          
          {isComplete ? (
            <circle
              cx={center}
              cy={center}
              r={pieRadius}
              fill="#3b82f6"
            />
          ) : (
            totalCount > 0 && completedCount > 0 && (
              <path
                d={createPieSlice(0, (completedCount / totalCount) * 360)}
                fill="#3b82f6"
              />
            )
          )}
          
          {isComplete && (
            <g style={{ transform: 'rotate(90deg)', transformOrigin: `${center}px ${center}px` }}>
              <path
                d={`M ${center - size * 0.1} ${center} L ${center - size * 0.05} ${center + size * 0.12} L ${center + size * 0.17} ${center - size * 0.15}`}
                stroke="white"
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          )}
        </svg>
      );
      }
    
    // Special case: Area entities get Tag icon
    if (entity.entityType === 'Area') {
      return <Tag size={size} weight="fill" color="#6b7280" />;
    }
    
    // Special case: Thread entities get chat icon directly (no async lookup needed)
    if (entity.entityType === 'Thread') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'ChatCircle',
            phosphorWeight: 'duotone',
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // Special case: Mail entities get envelope icon
    if (entity.entityType === 'Mail.Message' || entity.entityType === 'Mail.Thread') {
      return <EnvelopeSimple size={size} weight="fill" />;
    }
    
    // Special case: Rule entities get scroll icon directly (no async lookup needed)
    if (entity.entityType === 'Rule') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'Scroll',
            phosphorWeight: 'duotone',
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // Special case: AppInstance entities - resolve via iconService (has static fallback, no timing issues)
    if (entity.entityType === 'AppInstance' || entity.entityType === 'System.AppInstance') {
      const appId = (entity.skeleton as any)?.appId;
      const appIconConfig = appId
        ? { ...iconService.getAppIcon(appId), metadata: { source: 'app' as const, appId, isAppIcon: false } }
        : { type: 'phosphor' as const, phosphorIcon: 'AppWindow', phosphorWeight: 'duotone' as const, metadata: { source: 'default' as const, isAppIcon: false } };
      return (
        <Icon
          icon={appIconConfig}
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined}
        />
      );
    }
    
    // Special case: Window/NewWindow entities - check for app icon in skeleton first
    if (entity.entityType === 'Window' || entity.entityType === 'NewWindow') {
      // ✅ NEW: Use app icon from skeleton if available (for app windows)
      const appIcon = (entity.skeleton as any)?.appIcon;
      if (appIcon) {
        return <Icon icon={appIcon} size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} />;
      }
      
      // Fallback to GridFour icon (for launcher windows without specific app)
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'GridFour',
            phosphorWeight: 'duotone',
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // Special case: SearchResults entities get MagnifyingGlass icon
    if (entity.entityType === 'SearchResults') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'MagnifyingGlass',
            phosphorWeight: 'duotone',
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // Special case: OpenDialog entities get ArrowBendUpLeft icon
    if (entity.entityType === 'OpenDialog') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'ArrowBendLeftUp',
            phosphorWeight: 'duotone',
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // Special case: Note entities get note icon directly (no async lookup needed)
    if (entity.entityType === 'Note') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'Note',
            phosphorWeight: 'fill',
            color: '#f59e0b', // Amber/gold
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // Special case: Canvas entities get paint brush icon directly (no async lookup needed)
    if (entity.entityType === 'Canvas') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'PaintBrush',
            phosphorWeight: 'fill',
            color: '#7c3aed', // Dark violet/purple
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // ✅ NEW: Sheet/Spreadsheet entities get table icon
    if (entity.entityType === 'Sheet' || entity.entityType === 'Spreadsheet') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'Table',
            phosphorWeight: 'fill',
            color: '#10b981', // Green
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // ✅ NEW: Presentation/Slides/Slide entities get presentation icon
    if (entity.entityType === 'Presentation' || entity.entityType === 'Slides' || entity.entityType === 'Slide') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'Presentation',
            phosphorWeight: 'fill',
            color: '#f97316', // Orange
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // Special case: Contact entities — uses same avatar resolution as SenderAvatar
    // (stored avatarUrl → company favicon → Gravatar proxy → initials)
    if (entity.entityType === 'Contact' || entity.entityType === 'Mail.Contact') {
      return <ContactEntityIcon entity={entity} size={size} />;
    }

    // Special case: Task entities
    if (entity.entityType === 'Task') {
      return (
        <Icon
          icon={{
            type: 'phosphor',
            phosphorIcon: 'CheckSquare',
            phosphorWeight: 'duotone',
            color: '#3b82f6',
            metadata: { source: 'entity', entityType: 'Task', isAppIcon: false },
          }}
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined}
        />
      );
    }

    // Special case: Goal entities
    if (entity.entityType === 'Goal') {
      return (
        <Icon
          icon={{
            type: 'phosphor',
            phosphorIcon: 'Target',
            phosphorWeight: 'duotone',
            color: '#10b981',
            metadata: { source: 'entity', entityType: 'Goal', isAppIcon: false },
          }}
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined}
        />
      );
    }

    // Special case: Job entities
    if (entity.entityType === 'Job') {
      return (
        <Icon
          icon={{
            type: 'phosphor',
            phosphorIcon: 'Briefcase',
            phosphorWeight: 'duotone',
            color: '#f59e0b',
            metadata: { source: 'entity', entityType: 'Job', isAppIcon: false },
          }}
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined}
        />
      );
    }

    // Check for self-referencing icon (entity.icon === entity._id) BEFORE file-based icon
    // This allows image entities to use their thumbnail as icon
    if (entity.icon && entity.icon === entity._id) {
      return <Icon entity={entity} size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} />;
    }
    
    // For File entities, use FileIcon component which handles hierarchical types
    if (EntityTypeUtils.isSubtypeOf(entity.entityType, 'File') && (entity as any).name) {
      return (
        <FileIcon 
          filename={(entity as any).name} 
          entityType={entity.entityType}
          size={size} 
        />
      );
    }
    
    // For BookProject entities, use book icon
    if (entity.entityType === 'BookProject' || entity.entityType === 'Project.BookProject') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'Book',
            phosphorWeight: 'duotone',
            color: '#dc2626',
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // For Dashboard entities, use chart bar icon (accept both cases)
    if (entity.entityType === 'Dashboard' || entity.entityType === 'dashboard') {
      return (
        <Icon
          icon={{
            type: 'phosphor',
            phosphorIcon: 'ChartBar',
            phosphorWeight: 'duotone',
            color: '#3b82f6',
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }}
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined}
        />
      );
    }

    // For ManuscriptReviewReport entities, use newspaper icon
    if (entity.entityType === 'ManuscriptReviewReport') {
      return (
        <Icon
          icon={{
            type: 'phosphor',
            phosphorIcon: 'Newspaper',
            phosphorWeight: 'duotone',
            color: '#9b59b6',
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }}
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined}
        />
      );
    }

    // For WritersDashboard entities, use book open icon
    if (entity.entityType === 'WritersDashboard') {
      return (
        <Icon 
          icon={{ 
            type: 'phosphor', 
            phosphorIcon: 'BookOpen',
            phosphorWeight: 'duotone',
            color: '#4285f4', // Blue to match dashboard
            metadata: {
              source: 'entity',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
        width={needsCustomDimensions ? size : undefined}
        height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // For Doc entities, use the custom SVG icon
    if (entity.entityType === 'Doc') {
      return (
        <Icon 
          icon={{ 
            type: 'svg', 
            svgPath: '/icons/riverAppDocSmall.svg',
            metadata: {
              source: 'default',
              entityType: entity.entityType,
              isAppIcon: false
            }
          }} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    }
    
    // Automation entities use Lightning icon
    if (entity.entityType === 'Automation' || entity.entityType === 'Agent') {
      return (
        <Icon
          icon={{
            type: 'phosphor',
            phosphorIcon: 'Lightning',
            phosphorWeight: 'duotone',
            color: '#f59e0b',
            metadata: { source: 'entity', entityType: entity.entityType, isAppIcon: false }
          }}
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined}
        />
      );
    }

    // OrchestratorAgent entities use Brain icon
    if (entity.entityType === 'OrchestratorAgent') {
      return (
        <Icon
          icon={{
            type: 'phosphor',
            phosphorIcon: 'Brain',
            phosphorWeight: 'duotone',
            color: '#f59e0b',
            metadata: { source: 'entity', entityType: entity.entityType, isAppIcon: false }
          }}
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined}
        />
      );
    }

    // For all other entities, use the centralized Icon component with entity prop
    // This will automatically use iconService internally
    return <Icon entity={entity} size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} />;
  }, [expandedGroups, size, entities]);

  /**
   * Get icon for entity using async iconService (for advanced use cases)
   * Most components should use the synchronous getEntityIcon above
   */
  const getEntityIconAsync = useCallback(async (entity: BaseEntityType, context?: string): Promise<React.ReactNode> => {
    try {
      // Special case: Synthetic "Chats" group gets a chat icon instead of folder
      if (entity._id === '__spacechat__' || 
          entity._id === '__chats_folder__' || 
          entity._id === '__drive_chats_folder__' ||
          entity._id.startsWith('__drive_chats_folder_')) {
        return (
          <Icon 
            icon={{ 
              type: 'phosphor', 
              phosphorIcon: 'Chat',
              phosphorWeight: 'fill',
              metadata: {
                source: 'entity',
                entityType: 'Group',
                isAppIcon: false
              }
            }} 
            size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
          />
        );
      }
      
      // Special case: Groups with expansion state
      if (entity.entityType === 'Group') {
        const schemaIcon = (entity.skeleton as any)?.isEntityTypeFolder && (entity.skeleton as any)?.icon;
        if (schemaIcon) {
          return (
            <Icon
              icon={{ type: 'phosphor', phosphorIcon: schemaIcon, phosphorWeight: 'duotone',
                metadata: { source: 'entity', entityType: 'Group', isAppIcon: false } }}
              size={getIconSize()}
              width={needsCustomDimensions ? size : undefined}
              height={needsCustomDimensions ? size : undefined}
            />
          );
        }
        const isExpanded = expandedGroups.has(entity._id);
        return isExpanded 
          ? <FolderOpen size={size} weight="fill" /> 
          : <Folder size={size} weight="fill" />;
      }
      
      // Special case: Project entities get dynamic progress circle with pie chart
      if (entity.entityType === 'Project') {
        // Find all tasks for this project
        // Note: Tasks have hidden:true but are still in entityStore.entities (filtered only at display level)
        const projectTasks = entities.filter((e: BaseEntityType) => 
          e.entityType === 'Task' && 
          (e.skeleton as any)?.projectId === entity._id
        );
        
        const completedCount = projectTasks.filter((t: BaseEntityType) => 
          (t.skeleton as any)?.completed === true
        ).length;
        const totalCount = projectTasks.length;
        
        const radius = size === 16 ? 6 : (size / 16) * 6;
        const strokeWidth = size === 16 ? 1.5 : (size / 16) * 1.5;
        const center = size / 2;
        const pieRadius = size === 16 ? 5 : (size / 16) * 5;
        
        const createPieSlice = (startAngle: number, endAngle: number): string => {
          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;
          
          const x1 = center + pieRadius * Math.cos(startRad);
          const y1 = center + pieRadius * Math.sin(startRad);
          const x2 = center + pieRadius * Math.cos(endRad);
          const y2 = center + pieRadius * Math.sin(endRad);
          
          const largeArc = endAngle - startAngle > 180 ? 1 : 0;
          
          return `M ${center} ${center} L ${x1} ${y1} A ${pieRadius} ${pieRadius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
        };
        
        const isComplete = totalCount > 0 && completedCount === totalCount;
        
        return (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="#3b82f6"
              strokeWidth={strokeWidth}
            />
            
            {isComplete ? (
              <circle
                cx={center}
                cy={center}
                r={pieRadius}
                fill="#3b82f6"
              />
            ) : (
              totalCount > 0 && completedCount > 0 && (
                <path
                  d={createPieSlice(0, (completedCount / totalCount) * 360)}
                  fill="#3b82f6"
                />
              )
            )}
            
            {isComplete && (
              <g style={{ transform: 'rotate(90deg)', transformOrigin: `${center}px ${center}px` }}>
                <path
                  d={`M ${center - size * 0.1} ${center}
                      L ${center - size * 0.05} ${center + size * 0.12}
                      L ${center + size * 0.11} ${center - size * 0.12}`}
                  stroke="white"
                  strokeWidth={strokeWidth}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            )}
          </svg>
        );
      }
      
      // Special case: Area entities get Tag icon
      if (entity.entityType === 'Area') {
        return <Tag size={size} weight="fill" color="#6b7280" />;
      }
      
      // Special case: Thread entities
      if (entity.entityType === 'Thread') {
        return (
          <Icon 
            icon={{ 
              type: 'phosphor', 
              phosphorIcon: 'ChatCircle',
              phosphorWeight: 'duotone',
              metadata: {
                source: 'entity',
                entityType: entity.entityType,
                isAppIcon: false
              }
            }} 
            size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
          />
        );
      }
      
      // Special case: Rule entities
      if (entity.entityType === 'Rule') {
        return (
          <Icon 
            icon={{ 
              type: 'phosphor', 
              phosphorIcon: 'Scroll',
              phosphorWeight: 'duotone',
              metadata: {
                source: 'entity',
                entityType: entity.entityType,
                isAppIcon: false
              }
            }} 
            size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
          />
        );
      }
      
      // Special case: Window/NewWindow entities get GridFour icon
      if (entity.entityType === 'Window' || entity.entityType === 'NewWindow') {
        return (
          <Icon 
            icon={{ 
              type: 'phosphor', 
              phosphorIcon: 'GridFour',
              phosphorWeight: 'duotone',
              metadata: {
                source: 'entity',
                entityType: entity.entityType,
                isAppIcon: false
              }
            }} 
            size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
          />
        );
      }
      
      // Special case: SearchResults entities get MagnifyingGlass icon
      if (entity.entityType === 'SearchResults') {
        return (
          <Icon 
            icon={{ 
              type: 'phosphor', 
              phosphorIcon: 'MagnifyingGlass',
              phosphorWeight: 'duotone',
              metadata: {
                source: 'entity',
                entityType: entity.entityType,
                isAppIcon: false
              }
            }} 
            size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
          />
        );
      }
      
      // Special case: OpenDialog entities get ArrowBendUpLeft icon
      if (entity.entityType === 'OpenDialog') {
        return (
          <Icon 
            icon={{ 
              type: 'phosphor', 
              phosphorIcon: 'ArrowBendLeftUp',
              phosphorWeight: 'duotone',
              metadata: {
                source: 'entity',
                entityType: entity.entityType,
                isAppIcon: false
              }
            }} 
            size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
          />
        );
      }
      
      // Special case: Automation entities (AutomationsApp)
      if (entity.entityType === 'Automation' || entity.entityType === 'Agent') {
        return (
          <Icon
            icon={{
              type: 'phosphor',
              phosphorIcon: 'Lightning',
              phosphorWeight: 'duotone',
              color: '#f59e0b',
              metadata: { source: 'entity', entityType: 'Automation', isAppIcon: false }
            }}
            size={getIconSize()}
            width={needsCustomDimensions ? size : undefined}
            height={needsCustomDimensions ? size : undefined}
          />
        );
      }

      // Special case: Note entities
      if (entity.entityType === 'Note') {
        return (
          <Icon 
            icon={{ 
              type: 'phosphor', 
              phosphorIcon: 'Note',
              phosphorWeight: 'fill',
              color: '#f59e0b', // Amber/gold
              metadata: {
                source: 'entity',
                entityType: entity.entityType,
                isAppIcon: false
              }
            }} 
            size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
          />
        );
      }
      
      // Special case: Canvas entities
      if (entity.entityType === 'Canvas') {
        return (
          <Icon 
            icon={{ 
              type: 'phosphor', 
              phosphorIcon: 'PaintBrush',
              phosphorWeight: 'fill',
              color: '#7c3aed', // Dark violet/purple
              metadata: {
                source: 'entity',
                entityType: entity.entityType,
                isAppIcon: false
              }
            }} 
            size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
          />
        );
      }

      // Special case: Contact entities — delegate to synchronous handler
      if (entity.entityType === 'Contact' || entity.entityType === 'Mail.Contact') {
        return getEntityIcon(entity);
      }

      // Special case: Task entities
      if (entity.entityType === 'Task') {
        return (
          <Icon
            icon={{ type: 'phosphor', phosphorIcon: 'CheckSquare', phosphorWeight: 'duotone', color: '#3b82f6', metadata: { source: 'entity', entityType: 'Task', isAppIcon: false } }}
            size={getIconSize()}
            width={needsCustomDimensions ? size : undefined}
            height={needsCustomDimensions ? size : undefined}
          />
        );
      }

      // Special case: Goal entities
      if (entity.entityType === 'Goal') {
        return (
          <Icon
            icon={{ type: 'phosphor', phosphorIcon: 'Target', phosphorWeight: 'duotone', color: '#10b981', metadata: { source: 'entity', entityType: 'Goal', isAppIcon: false } }}
            size={getIconSize()}
            width={needsCustomDimensions ? size : undefined}
            height={needsCustomDimensions ? size : undefined}
          />
        );
      }

      // Special case: Job entities
      if (entity.entityType === 'Job') {
        return (
          <Icon
            icon={{ type: 'phosphor', phosphorIcon: 'Briefcase', phosphorWeight: 'duotone', color: '#f59e0b', metadata: { source: 'entity', entityType: 'Job', isAppIcon: false } }}
            size={getIconSize()}
            width={needsCustomDimensions ? size : undefined}
            height={needsCustomDimensions ? size : undefined}
          />
        );
      }
      
      // Use iconService for advanced resolution
      const iconConfig = await iconService.getEntityIcon(entity, context);
      
      return (
        <Icon 
          icon={iconConfig} 
          size={getIconSize()}
          width={needsCustomDimensions ? size : undefined}
          height={needsCustomDimensions ? size : undefined} 
        />
      );
    } catch (error) {
      console.warn('[useEntityIcon] Failed to resolve icon, using fallback:', error);
      // Fallback to synchronous method
      return getEntityIcon(entity);
    }
  }, [expandedGroups, size, entities]);

  return {
    getEntityIcon,
    getEntityIconAsync
  };
};

export default useEntityIcon;
